package ui.app.assign.detail.wheel.preview.pages

import androidx.compose.foundation.Image
import androidx.compose.foundation.VerticalScrollbar
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.rememberScrollbarAdapter
import androidx.compose.material3.Card
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.unit.dp
import apps.App
import common.frontend.api.Io
import compose.assets.Svg
import compose.assets.svg.*
import ssoft.frontend.generated.resources.*
import tools.rememberString
import ui.app.assign.detail.wheel.AppWheelRendererGame
import ui.app.assign.detail.wheel.AppWheelRendererStartup
import ui.app.assign.detail.wheel.preview.AppPreviewWheelScope

@Composable
fun AppPreviewWheelScope.AppWheelPreview(nnvl: AppPreviewWheelScope.Wrapper, modifier: Modifier) {
    val state = rememberLazyListState()
    Box(modifier.fillMaxSize()) {
        LazyColumn(
            modifier = Modifier.fillMaxSize(),
            contentPadding = PaddingValues(8.dp)
        ) {
            item {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    BasicInfo(nnvl)
                    PreviewEntry(nnvl.data, nnvl.appRef)
                }
            }
        }
        VerticalScrollbar(
            modifier = Modifier.fillMaxHeight()
                .align(Alignment.CenterEnd)
                .padding(2.dp),
            adapter = rememberScrollbarAdapter(state)
        )
    }
}

@Composable
private fun AppPreviewWheelScope.BasicInfo(
    nnvl: AppPreviewWheelScope.Wrapper
) {
    Card(Modifier.fillMaxWidth()) {
        Column(
            verticalArrangement = Arrangement.spacedBy(8.dp),
            modifier = Modifier.padding(8.dp)
        ) {
            Name(nnvl.data)
            Orientation(nnvl.data, nnvl.appRef)
            Device(nnvl.data)
            Date(nnvl.data)
            Note(nnvl.data)
        }
    }
}

@Composable
private fun AppPreviewWheelScope.Name(
    data: Io.AppDetail
) {
    LineInfo(Svg.Format_text, data.name)
}


@Composable
private fun AppPreviewWheelScope.Orientation(
    data: Io.AppDetail,
    appRef: App.Wheel
) {
    if (appRef.content.theme.isLand) {
        LineInfo(Svg.ScreenLand, Res.string.layer_bottom_wheel_page_info_orientation_land.rememberString())
    } else {
        LineInfo(Svg.ScreenPort, Res.string.layer_bottom_wheel_page_info_orientation_port.rememberString())
    }
}

@Composable
private fun AppPreviewWheelScope.Device(
    data: Io.AppDetail
) {
    data.locations.firstNotNullOfOrNull { loc -> loc.devices.firstOrNull { it.enabled } }
        ?.also {
            LineInfo(Svg.Device, it.name)
        } ?: LineInfo(Svg.Device, Res.string.layer_bottom_wheel_info_no_device.rememberString())
}

@Composable
private fun AppPreviewWheelScope.Date(
    data: Io.AppDetail
) {
    if (data.scheduled) {
        LineInfo(Svg.Calendar, "${data.start} - ${data.end}")
    }
}

@Composable
private fun AppPreviewWheelScope.Note(
    data: Io.AppDetail
) {
    if (data.note.trim().isNotEmpty()) {
        LineInfo(Svg.Text_long, data.note)
    }
}

@Composable
private fun AppPreviewWheelScope.PreviewEntry(
    data: Io.AppDetail,
    config: App.Wheel
) {
    if (config.content.theme.isLand) {
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Card(modifier = Modifier.fillMaxWidth()) {
                Column {
                    Text(
                        text = Res.string.layer_bottom_wheel_page_summary_startup.rememberString(),
                        modifier = Modifier.padding(8.dp)
                    )
                    AppWheelRendererStartup(
                        config = config,
                        qr = data.qrUrl,
                        modifier = Modifier.fillMaxWidth()
                            .aspectRatio(16F / 9F),
                    )
                }
            }
            Card(modifier = Modifier.fillMaxWidth()) {
                Column {
                    Text(
                        text = Res.string.layer_bottom_wheel_page_summary_game.rememberString(),
                        modifier = Modifier.padding(8.dp)
                    )

                    AppWheelRendererGame(
                        modifier = Modifier.fillMaxWidth()
                            .aspectRatio(16F / 9F),
                        bgImageId = config.content.theme.backgroundApp,
                        bgColor = Color(config.content.theme.backgroundColor),
                        wheelImage = vm.wheelImage,
                        wheelScale = config.content.wheel.scale.toFloat() / 100F,
                        wheelX = config.content.wheel.x.toFloat() / 100F,
                        wheelY = config.content.wheel.y.toFloat() / 100F
                    )
                }
            }
        }
    } else {
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            Card(modifier = Modifier.weight(1F)) {
                Column {
                    Text(
                        text = Res.string.layer_bottom_wheel_page_summary_startup.rememberString(),
                        modifier = Modifier.padding(8.dp)
                    )
                    AppWheelRendererStartup(
                        config = config,
                        qr = data.qrUrl,
                        modifier = Modifier.fillMaxWidth()
                            .aspectRatio(9F / 16F),
                    )
                }
            }
            Card(modifier = Modifier.weight(1F)) {
                Column {
                    Text(
                        text = Res.string.layer_bottom_wheel_page_summary_game.rememberString(),
                        modifier = Modifier.padding(8.dp)
                    )
                    AppWheelRendererGame(
                        modifier = Modifier.fillMaxWidth()
                            .aspectRatio(9F / 16F),
                        bgImageId = config.content.theme.backgroundApp,
                        bgColor = Color(config.content.theme.backgroundColor),
                        wheelImage = vm.wheelImage,
                        wheelScale = config.content.wheel.scale.toFloat() / 100F,
                        wheelX = config.content.wheel.x.toFloat() / 100F,
                        wheelY = config.content.wheel.y.toFloat() / 100F
                    )
                }
            }
        }
    }
}

@Composable
private fun LineInfo(icon: ImageVector, text: String) {
    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
        Icon(icon, null)
        Text(
            text = text,
            style = MaterialTheme.typography.titleMedium
        )
    }
}